import React, { useState } from "react";
import "../styles/Header.css";
import logo from "../img/logo-laboratorio.jpg";
import Button from "@mui/material/Button";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import UsuariosService from "../services/UsuariosService";
import SucursalesService from "../services/SucursalesService";
import EmpleadosService from "../services/EmpleadosService";
import { tieneMasDe10Digitos } from "../utils/telefonoUtils";

const Header = () => {
  const [usuarioState, setUsuarioState] = useState(() => {
    const usuarioString = sessionStorage.getItem('usuario') || localStorage.getItem('usuario');
    return usuarioString ? JSON.parse(usuarioString) : null;
  });

  const usuario = usuarioState;
  const isUserAdmin = usuarioState && (usuarioState.rol === "A" || usuarioState.rol === "ADMIN");

  const navigate = useNavigate();

  const handleCambiarSucursalAdmin = async (e) => {
    if (e) e.stopPropagation();

    if (!isUserAdmin) {
      return;
    }

    try {
      const res = await SucursalesService.getSucursales();
      const listaSucursales = res?.data?.sucursales || res?.data || res || [];

      if (!Array.isArray(listaSucursales) || listaSucursales.length === 0) {
        Swal.fire("Error", "No se encontraron sucursales disponibles en el sistema.", "error");
        return;
      }

      const inputOptions = {};
      listaSucursales.forEach((s) => {
        if (String(s.estatus).toUpperCase() !== "I") {
          const idSuc = s.idSucursal || s.id;
          inputOptions[idSuc] = s.nombre || s.nombreSucursal || `Sucursal ${idSuc}`;
        }
      });

      const currentSucId = usuarioState?.idSucursal || usuarioState?.sucursalId || "";

      const { value: selectedId } = await Swal.fire({
        title: "Cambiar de Sucursal",
        text: "Elija la sucursal a la que desea cambiar",
        input: "select",
        inputOptions: inputOptions,
        inputValue: currentSucId,
        showCancelButton: true,
        confirmButtonText: "Cambiar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#18234f",
        cancelButtonColor: "#d33",
        inputValidator: (value) => {
          if (!value) {
            return "Debe seleccionar una sucursal";
          }
        },
      });

      if (selectedId) {
        const sucursalElegida = listaSucursales.find(
          (s) => String(s.idSucursal || s.id) === String(selectedId)
        );

        const nuevoNombreSucursal = sucursalElegida ? (sucursalElegida.nombre || sucursalElegida.nombreSucursal) : `Sucursal ${selectedId}`;

        // 1. Guardar de forma permanente en la Base de Datos a través de UsuariosService
        const userId = usuarioState?.id || usuarioState?.idUsuario;
        if (userId) {
          try {
            await UsuariosService.cambiarSucursal(userId, Number(selectedId));
          } catch (eErr) {
            console.warn("No se pudo actualizar sucursal en BD:", eErr);
          }
          localStorage.setItem(
            `admin_sucursal_${userId}`,
            JSON.stringify({ idSucursal: Number(selectedId), nombreSucursal: nuevoNombreSucursal })
          );
        }

        // 2. Actualizar sesión activa
        const usuarioActualizado = {
          ...usuarioState,
          idSucursal: Number(selectedId),
          sucursal: Number(selectedId),
          sucursalId: Number(selectedId),
          nombreSucursal: nuevoNombreSucursal,
        };

        sessionStorage.setItem("usuario", JSON.stringify(usuarioActualizado));
        localStorage.setItem("usuario", JSON.stringify(usuarioActualizado));
        setUsuarioState(usuarioActualizado);

        await Swal.fire({
          icon: "success",
          title: "Sucursal actualizada",
          text: `Se guardó el cambio`,
          confirmButtonColor: "#18234f",
          timer: 1500,
          showConfirmButton: false,
        });

        window.location.reload();
      }
    } catch (error) {
      console.error("Error al cambiar sucursal de admin:", error);
      Swal.fire("Error", "Ocurrió un problema al obtener las sucursales del sistema.", "error");
    }
  };

  const cerrarSesion = () => {
    // Preservar preferencias de sucursales de admin
    const adminSucursales = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("admin_sucursal_")) {
        adminSucursales[key] = localStorage.getItem(key);
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    // Restaurar preferencias
    Object.keys(adminSucursales).forEach((k) => {
      localStorage.setItem(k, adminSucursales[k]);
    });

    // Redirigir reemplazando el historial del navegador
    navigate("/", { replace: true });
  };

  const handleAbrirOpcionesUsuario = async () => {
    if (!usuario || !usuario.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró información de la sesión activa.",
      });
      return;
    }

    const resPregunta = await Swal.fire({
      title: "¿Qué desea cambiar?",
      text: "Seleccione una opción para continuar",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Usuario",
      denyButtonText: "Contraseña",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#18234f",
      denyButtonColor: "#10547f",
      cancelButtonColor: "#d32f2f",
    });

    if (resPregunta.isConfirmed) {
      const userId = usuario.id || usuario.idUsuario;
      let userData = { ...usuario };

      try {
        const resUser = await UsuariosService.getUsuarioById(userId);
        if (resUser && resUser.usuario) {
          const uObj = Array.isArray(resUser.usuario) ? resUser.usuario[0] : resUser.usuario;
          if (uObj) {
            userData = { ...userData, ...uObj };
          }
        }
      } catch (e) {
        console.error("Error al obtener datos del usuario:", e);
      }

      const htmlForm = `
        <div style="text-align: left; font-family: Roboto, sans-serif; max-height: 65vh; overflow-y: auto; padding: 5px 10px; box-sizing: border-box;">
          <p style="font-size: 1rem; font-weight: bold; color: #18234f; margin-top: 0; margin-bottom: 1rem; border-bottom: 2px solid #18234f; padding-bottom: 0.4rem;">
            DATOS PERSONALES DEL USUARIO
          </p>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem 1.2rem; align-items: start;">
            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-usuario" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Usuario: <span style="color: red;">*</span>
              </label>
              <input id="swal-usr-usuario" aria-label="Usuario" class="swal2-input" value="${userData.usuario || ""}" placeholder="Nombre de usuario" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
              <span id="err-swal-usr-usuario" class="swal-inline-error" style="color: #d32f2f; font-size: 0.75rem; margin-top: 0.2rem; display: none;"></span>
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-nombre" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Nombre: <span style="color: red;">*</span>
              </label>
              <input id="swal-usr-nombre" aria-label="Nombre" class="swal2-input" value="${userData.nombre || ""}" placeholder="Nombre" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
              <span id="err-swal-usr-nombre" class="swal-inline-error" style="color: #d32f2f; font-size: 0.75rem; margin-top: 0.2rem; display: none;"></span>
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-apPaterno" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Apellido Paterno: <span style="color: red;">*</span>
              </label>
              <input id="swal-usr-apPaterno" aria-label="Apellido Paterno" class="swal2-input" value="${userData.apPaterno || ""}" placeholder="Apellido Paterno" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
              <span id="err-swal-usr-apPaterno" class="swal-inline-error" style="color: #d32f2f; font-size: 0.75rem; margin-top: 0.2rem; display: none;"></span>
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-apMaterno" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Apellido Materno:
              </label>
              <input id="swal-usr-apMaterno" aria-label="Apellido Materno" class="swal2-input" value="${userData.apMaterno || ""}" placeholder="Apellido Materno" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-sexo" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Sexo:
              </label>
              <select id="swal-usr-sexo" aria-label="Sexo" class="swal2-input" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;">
                <option value="M" ${String(userData.sexo).toUpperCase() === "M" ? "selected" : ""}>Masculino</option>
                <option value="F" ${String(userData.sexo).toUpperCase() === "F" ? "selected" : ""}>Femenino</option>
              </select>
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-fechaNacimiento" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Fecha de Nacimiento:
              </label>
              <input id="swal-usr-fechaNacimiento" type="date" aria-label="Fecha de Nacimiento" class="swal2-input" value="${userData.fechaNacimiento || ""}" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-calle" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Calle:
              </label>
              <input id="swal-usr-calle" aria-label="Calle" class="swal2-input" value="${userData.calle || ""}" placeholder="Calle" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-colonia" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Colonia:
              </label>
              <input id="swal-usr-colonia" aria-label="Colonia" class="swal2-input" value="${userData.colonia || ""}" placeholder="Colonia" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-cPostal" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Código Postal:
              </label>
              <input id="swal-usr-cPostal" aria-label="Código Postal" class="swal2-input" value="${userData.cPostal || ""}" placeholder="Código Postal" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-ciudad" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Ciudad:
              </label>
              <input id="swal-usr-ciudad" aria-label="Ciudad" class="swal2-input" value="${userData.ciudad || ""}" placeholder="Ciudad" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>

            
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-telefonoCelular" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Teléfono Celular:
              </label>
              <input id="swal-usr-telefonoCelular" aria-label="Teléfono Celular" class="swal2-input" value="${userData.telefonoCelular || userData.telefonoTrabajo || ""}" placeholder="Teléfono Celular" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
              <span id="err-swal-usr-telefonoCelular" class="swal-inline-error" style="color: #d32f2f; font-size: 0.75rem; margin-top: 2px; display: none;"></span>
            </div>

           
            <div style="display: flex; flex-direction: column;">
              <label for="swal-usr-email" style="font-weight: 600; font-size: 0.85rem; color: #333; margin-bottom: 0.3rem;">
                Email:
              </label>
              <input id="swal-usr-email" type="email" aria-label="Email" class="swal2-input" value="${userData.email || ""}" placeholder="Correo electrónico" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 0.9rem; height: 38px; padding: 0 10px;" />
            </div>
          </div>
        </div>
      `;

      const { value: nuevoUsuarioGuardado } = await Swal.fire({
        title: "Editar Datos de Usuario",
        width: "750px",
        html: htmlForm,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Guardar cambios",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#4CAF50",
        cancelButtonColor: "#d32f2f",
        preConfirm: async () => {
          document.querySelectorAll(".swal-inline-error").forEach((el) => {
            el.style.display = "none";
            el.innerText = "";
          });

          const valUsuario = document.getElementById("swal-usr-usuario")?.value?.trim() || "";
          const valNombre = document.getElementById("swal-usr-nombre")?.value?.trim() || "";
          const valApPaterno = document.getElementById("swal-usr-apPaterno")?.value?.trim() || "";

          let tieneError = false;

          if (!valUsuario) {
            const errEl = document.getElementById("err-swal-usr-usuario");
            if (errEl) {
              errEl.innerText = "Por favor introduce el usuario";
              errEl.style.display = "block";
            }
            tieneError = true;
          }

          if (!valNombre) {
            const errEl = document.getElementById("err-swal-usr-nombre");
            if (errEl) {
              errEl.innerText = "Por favor introduce el nombre";
              errEl.style.display = "block";
            }
            tieneError = true;
          }

          if (!valApPaterno) {
            const errEl = document.getElementById("err-swal-usr-apPaterno");
            if (errEl) {
              errEl.innerText = "Por favor introduce el apellido paterno";
              errEl.style.display = "block";
            }
            tieneError = true;
          }

          const valApMaterno = document.getElementById("swal-usr-apMaterno")?.value?.trim() || "";
          const valSexo = document.getElementById("swal-usr-sexo")?.value || "";
          const valFechaNac = document.getElementById("swal-usr-fechaNacimiento")?.value || "";
          const valCalle = document.getElementById("swal-usr-calle")?.value?.trim() || "";
          const valColonia = document.getElementById("swal-usr-colonia")?.value?.trim() || "";
          const valCPostal = document.getElementById("swal-usr-cPostal")?.value?.trim() || "";
          const valCiudad = document.getElementById("swal-usr-ciudad")?.value?.trim() || "";
          const valTelefonoCel = document.getElementById("swal-usr-telefonoCelular")?.value?.trim() || "";
          const valEmail = document.getElementById("swal-usr-email")?.value?.trim() || "";

          if (tieneMasDe10Digitos(valTelefonoCel)) {
            const errEl = document.getElementById("err-swal-usr-telefonoCelular");
            if (errEl) {
              errEl.innerText = "El teléfono celular no puede tener más de 10 dígitos";
              errEl.style.display = "block";
            }
            tieneError = true;
          }

          if (tieneError) {
            return false;
          }

          const userPayload = {
            ...userData,
            usuario: valUsuario,
            nombre: valNombre,
            apPaterno: valApPaterno,
            apMaterno: valApMaterno,
            sexo: valSexo,
            fechaNacimiento: valFechaNac,
            calle: valCalle,
            colonia: valColonia,
            cPostal: valCPostal,
            ciudad: valCiudad,
            telefonoCelular: valTelefonoCel,
            email: valEmail,
          };

          try {
            // Se envía la actualización completa del usuario
            try {
              await UsuariosService.updateUsuario(userPayload, userId);
            } catch (errUpd) {
              console.warn("Actualización general de usuario:", errUpd);
            }

            const res = await UsuariosService.cambiarUsuario(userId, valUsuario);
            if (res && res.estatus === "ERROR") {
              const errEl = document.getElementById("err-swal-usr-usuario");
              if (errEl) {
                errEl.innerText = res.mensaje || "El usuario ya está en uso";
                errEl.style.display = "block";
              } else {
                Swal.showValidationMessage(res.mensaje || "El usuario ya está en uso");
              }
              return false;
            }
            return userPayload;
          } catch (e) {
            Swal.showValidationMessage("Error al guardar en el servidor");
            return false;
          }
        },
      });

      if (nuevoUsuarioGuardado) {
        await Swal.fire({
          icon: "success",
          title: "¡Cambios realizados con éxito!",
          text: "Se requiere volver a iniciar sesión para aplicar los cambios.",
          confirmButtonColor: "#18234f",
          confirmButtonText: "Aceptar",
        });

        localStorage.clear();
        sessionStorage.clear();
        navigate("/", { replace: true });
      }
    } else if (resPregunta.isDenied) {
      let usuarioActualHandle = usuario.usuario || "";
      if (!usuarioActualHandle) {
        try {
          const userObj = await UsuariosService.getUsuarioById(usuario.id);
          if (userObj) {
            usuarioActualHandle = typeof userObj.usuario === "string" ? userObj.usuario : (userObj.usuario?.usuario || "");
          }
        } catch (e) {
          console.error("Error al obtener usuario:", e);
        }
      }

      const { value: actualizadoExito } = await Swal.fire({
        title: "Cambiar Contraseña",
        html: `
          <div style="text-align: left; font-family: Roboto, sans-serif; display: flex; flex-direction: column; gap: 0.9rem; padding: 5px;">
            <div>
              <label for="swal-pass-actual" style="font-weight: 600; display: block; margin-bottom: 0.3rem; color: #333;">Contraseña actual:</label>
              <input id="swal-pass-actual" type="password" class="swal2-input" placeholder="Contraseña actual" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 1rem;" />
            </div>
            <div>
              <label for="swal-pass-nueva" style="font-weight: 600; display: block; margin-bottom: 0.3rem; color: #333;">Nueva contraseña:</label>
              <input id="swal-pass-nueva" type="password" class="swal2-input" placeholder="Nueva contraseña" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 1rem;" />
            </div>
            <div>
              <label for="swal-pass-confirmar" style="font-weight: 600; display: block; margin-bottom: 0.3rem; color: #333;">Confirmar nueva contraseña:</label>
              <input id="swal-pass-confirmar" type="password" class="swal2-input" placeholder="Confirmar nueva contraseña" style="width: 100%; box-sizing: border-box; margin: 0; font-size: 1rem;" />
            </div>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Guardar cambios",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#4CAF50",
        cancelButtonColor: "#d32f2f",
        preConfirm: async () => {
          const passActual = document.getElementById("swal-pass-actual")?.value || "";
          const passNueva = document.getElementById("swal-pass-nueva")?.value || "";
          const passConfirmar = document.getElementById("swal-pass-confirmar")?.value || "";

          if (!passActual || !passNueva || !passConfirmar) {
            Swal.showValidationMessage("Por favor complete todos los campos de contraseña");
            return false;
          }


          try {
            const loginVal = await UsuariosService.getLogin(usuarioActualHandle, passActual);
            if (!loginVal || loginVal.length === 0) {
              Swal.showValidationMessage("La contraseña no coincide con la actual");
              return false;
            }
          } catch (e) {
            Swal.showValidationMessage("La contraseña no coincide con la actual");
            return false;
          }


          if (passNueva !== passConfirmar) {
            Swal.showValidationMessage("Las contraseñas nuevas no coinciden");
            return false;
          }


          try {
            const res = await UsuariosService.cambiarContrasenia(usuario.id, passNueva);
            if (res && res.estatus === "ERROR") {
              Swal.showValidationMessage(res.mensaje || "Error al actualizar la contraseña");
              return false;
            }
            return true;
          } catch (e) {
            Swal.showValidationMessage("Error al actualizar la contraseña en el servidor");
            return false;
          }
        },
      });

      if (actualizadoExito) {
        await Swal.fire({
          icon: "success",
          title: "¡Cambios realizados con éxito!",
          text: "Se requiere volver a iniciar sesión para validar los cambios.",
          confirmButtonColor: "#18234f",
          confirmButtonText: "Aceptar",
        });

        localStorage.clear();
        sessionStorage.clear();
        navigate("/", { replace: true });
      }
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <ul>
          <li>
            <a
              href="/Menu"
              onClick={(e) => {
                e.preventDefault();
                navigate("/Menu", { state: { fromApp: true } });
              }}
            >
              <img src={logo} alt="logo" className="logo" />
            </a>
          </li>
        </ul>
      </div>

      <div className="navbar-rightAd">
        <div className="user-info">
          <div className="user-header">
            <AccountCircleIcon
              sx={{ color: "#18234f", fontSize: "2.6vw", cursor: "pointer" }}
              onClick={handleAbrirOpcionesUsuario}
              title="Haga clic para cambiar usuario o contraseña"
            />
            <div className="user-text">
              <span
                className="bienvenido"
                onClick={handleAbrirOpcionesUsuario}
                style={{ cursor: "pointer" }}
                title="Haga clic para cambiar usuario o contraseña"
              >
                Bienvenido, {usuario ? usuario.nombre : ""}
              </span>
              <span
                className="sucursal"
                onClick={isUserAdmin ? handleCambiarSucursalAdmin : undefined}
                style={{ cursor: isUserAdmin ? "pointer" : "default" }}
                title={isUserAdmin ? "Haga clic para cambiar de sucursal" : undefined}
              >
                Sucursal: {usuario ? usuario.nombreSucursal : ""}
              </span>
            </div>
          </div>
          <div className="user-actions">
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/Menu", { state: { fromApp: true } })}
              sx={{
                "&.MuiButton-root": {
                  minHeight: "38px",
                  height: "auto",
                  width: "auto",
                  minWidth: "75px",
                  padding: "0.4vw 1vw",
                  borderRadius: "0.5vw",
                },
                "& .MuiButton-startIcon": {
                  marginRight: "4px",
                },
                "& .MuiButton-startIcon svg": {
                  fontSize: "1.3vw",
                },
                fontSize: "0.8vw",
                backgroundColor: "#18234f",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#2E3B55",
                  boxShadow: "0 0.5vw 1vw rgba(0,0,0,0.3)",
                },
              }}
            >
              Inicio
            </Button>

            <Button
              variant="contained"
              startIcon={<ExitToAppIcon />}
              onClick={cerrarSesion}
              sx={{
                "&.MuiButton-root": {
                  minHeight: "38px",
                  height: "auto",
                  width: "auto",
                  minWidth: "110px",
                  padding: "0.4vw 1vw",
                  borderRadius: "0.5vw",
                },
                "& .MuiButton-startIcon": {
                  marginRight: "4px",
                },
                "& .MuiButton-startIcon svg": {
                  fontSize: "1.3vw",
                },
                fontSize: "0.8vw",
                backgroundColor: "#d32f2f",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                  boxShadow: "0 0.5vw 1vw rgba(0,0,0,0.3)",
                },
              }}
            >
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};
export default Header;
